import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, UserPlus, Trash2, Download, Search, ToggleLeft, ToggleRight, Users, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { adminFetch } from '../../utils/adminFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminNewsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchSubscribers(); }, []);

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`${API}/admin/newsletter-subscribers`);
      if (res.ok) {
        setSubscribers(await res.json());
      } else {
        const err = await res.text();
        console.error('Error fetching subscribers:', res.status, err);
        toast.error(`Error al cargar suscriptores: ${res.status}`);
      }
    } catch (e) {
      console.error('Network error:', e);
      toast.error('Error de conexión al cargar suscriptores');
    } finally {
      setLoading(false);
    }
  };

  const toggleSubscriber = async (subscriberId) => {
    try {
      const res = await adminFetch(`${API}/admin/newsletter-subscribers/${subscriberId}/toggle`, {
        method: 'PUT'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchSubscribers();
      }
    } catch (e) { toast.error('Error al cambiar estado'); }
  };

  const deleteSubscriber = async (subscriberId, email) => {
    if (!window.confirm(`¿Eliminar suscriptor ${email}?`)) return;
    try {
      const res = await adminFetch(`${API}/admin/newsletter-subscribers/${subscriberId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Suscriptor eliminado');
        fetchSubscribers();
      }
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const convertToClient = async (subscriberId) => {
    try {
      const res = await adminFetch(`${API}/admin/newsletter-subscribers/${subscriberId}/to-client`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchSubscribers();
      }
    } catch (e) { toast.error('Error al convertir'); }
  };

  const exportCSV = async () => {
    try {
      const res = await adminFetch(`${API}/admin/newsletter-subscribers/export`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'suscriptores_newsletter.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('CSV exportado');
      }
    } catch (e) { toast.error('Error al exportar'); }
  };

  const filtered = subscribers.filter(s => {
    const matchSearch = s.email.toLowerCase().includes(search.toLowerCase());
    if (filter === 'active') return matchSearch && s.active !== false;
    if (filter === 'inactive') return matchSearch && s.active === false;
    if (filter === 'clients') return matchSearch && s.is_client;
    if (filter === 'not-clients') return matchSearch && !s.is_client;
    return matchSearch;
  });

  const activeCount = subscribers.filter(s => s.active !== false).length;
  const clientCount = subscribers.filter(s => s.is_client).length;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-newsletter">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl mb-1">Suscriptores Newsletter</h1>
            <p className="text-muted-foreground text-sm">
              {subscribers.length} suscriptores totales · {activeCount} activos · {clientCount} ya son clientes
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchSubscribers} className="gap-2" data-testid="refresh-subscribers">
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </Button>
            <Button variant="outline" size="sm" onClick={exportCSV} className="gap-2" data-testid="export-csv">
              <Download className="w-4 h-4" />
              Exportar CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card border border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Mail className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Total</span>
            </div>
            <p className="font-serif text-2xl">{subscribers.length}</p>
          </div>
          <div className="bg-card border border-border p-4">
            <div className="flex items-center gap-2 text-green-500 mb-1">
              <ToggleRight className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Activos</span>
            </div>
            <p className="font-serif text-2xl">{activeCount}</p>
          </div>
          <div className="bg-card border border-border p-4">
            <div className="flex items-center gap-2 text-blue-500 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Son Clientes</span>
            </div>
            <p className="font-serif text-2xl">{clientCount}</p>
          </div>
          <div className="bg-card border border-border p-4">
            <div className="flex items-center gap-2 text-amber-500 mb-1">
              <UserPlus className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Potenciales</span>
            </div>
            <p className="font-serif text-2xl">{subscribers.length - clientCount}</p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar por email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-card border border-border text-sm focus:outline-none focus:ring-1 focus:ring-foreground"
              data-testid="search-subscribers"
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            {[
              { key: 'all', label: 'Todos' },
              { key: 'active', label: 'Activos' },
              { key: 'inactive', label: 'Inactivos' },
              { key: 'not-clients', label: 'No clientes' },
              { key: 'clients', label: 'Ya clientes' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-2 text-xs transition-colors ${
                  filter === f.key ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-14 bg-muted animate-pulse" />)}
          </div>
        ) : filtered.length > 0 ? (
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted border-b border-border">
                  <th className="text-left p-3 font-medium">Email</th>
                  <th className="text-left p-3 font-medium hidden sm:table-cell">Fecha</th>
                  <th className="text-center p-3 font-medium">Estado</th>
                  <th className="text-center p-3 font-medium hidden md:table-cell">Cliente</th>
                  <th className="text-right p-3 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(sub => (
                  <tr key={sub.subscriber_id} className="border-b border-border hover:bg-muted/50 transition-colors" data-testid={`subscriber-${sub.subscriber_id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span className="truncate max-w-[200px] md:max-w-none">{sub.email}</span>
                      </div>
                    </td>
                    <td className="p-3 text-muted-foreground hidden sm:table-cell">
                      {new Date(sub.subscribed_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 ${
                        sub.active !== false ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                      }`}>
                        {sub.active !== false ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="p-3 text-center hidden md:table-cell">
                      {sub.is_client ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-blue-500/10 text-blue-500">
                          <Users className="w-3 h-3" />
                          {sub.client_info?.status === 'active' ? 'Cliente activo' : 'Cliente potencial'}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        {!sub.is_client && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => convertToClient(sub.subscriber_id)}
                            title="Convertir en cliente potencial"
                            data-testid={`convert-${sub.subscriber_id}`}
                          >
                            <UserPlus className="w-4 h-4 text-blue-500" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleSubscriber(sub.subscriber_id)}
                          title={sub.active !== false ? 'Desactivar' : 'Activar'}
                          data-testid={`toggle-${sub.subscriber_id}`}
                        >
                          {sub.active !== false
                            ? <ToggleRight className="w-4 h-4 text-green-500" />
                            : <ToggleLeft className="w-4 h-4 text-muted-foreground" />
                          }
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteSubscriber(sub.subscriber_id, sub.email)}
                          title="Eliminar"
                          data-testid={`delete-${sub.subscriber_id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-16 bg-muted/50 border border-border">
            <Mail className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-serif text-xl mb-2">
              {search ? 'Sin resultados' : 'Sin suscriptores'}
            </h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              {search
                ? `No se encontraron suscriptores con "${search}"`
                : 'Cuando alguien se suscriba a la newsletter desde tu web, aparecerá aquí.'
              }
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminNewsletter;
